import { supabase } from '../lib/supabase';
import { chatService } from './chatService';

// Avisar uma vez só por sessão que a moderação não está instalada neste banco.
let moderacaoIndisponivelAvisada = false;

// Interfaces para moderação
export interface ModerationRule {
  id: string;
  type: 'word_filter' | 'regex_filter' | 'user_block' | 'auto_delete';
  pattern: string;
  action: 'warn' | 'delete' | 'block_user' | 'flag_for_review';
  severity: 'low' | 'medium' | 'high' | 'critical';
  is_active: boolean;
}

export interface ModerationAction {
  id: string;
  room_id: string;
  message_id?: string;
  user_id: string;
  moderator_id: string;
  action_type: 'delete_message' | 'warn_user' | 'block_user' | 'unblock_user' | 'flag_content';
  reason: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface BlockedUser {
  id: string;
  room_id: string;
  user_id: string;
  blocked_by: string;
  reason: string;
  expires_at?: string;
  created_at: string;
}

class ModerationService {
  // Palavras e frases proibidas padrão (pode ser expandido)
  private defaultBannedWords = [
    // Palavrões básicos
    'merda', 'caralho', 'porra', 'foda-se', 'vai se foder',
    // Termos ofensivos
    'idiota', 'burro', 'estúpido', 'imbecil', 'retardado',
    // Discriminação
    'racista', 'nazista', 'fascista',
    // Spam patterns
    'compre agora', 'clique aqui', 'oferta imperdível'
  ];

  // Verificar se uma mensagem viola as regras de moderação
  async checkMessageContent(content: string, roomId: string, userId: string): Promise<{
    isAllowed: boolean;
    violations: string[];
    suggestedAction: 'allow' | 'warn' | 'delete' | 'flag';
    severity: 'low' | 'medium' | 'high' | 'critical';
  }> {
    const violations: string[] = [];
    let maxSeverity: 'low' | 'medium' | 'high' | 'critical' = 'low';

    // 1. Verificar palavras proibidas
    const lowerContent = content.toLowerCase();
    for (const word of this.defaultBannedWords) {
      if (lowerContent.includes(word.toLowerCase())) {
        violations.push(`Conteúdo inadequado: palavra "${word}"`);
        maxSeverity = this.escalateSeverity(maxSeverity, 'medium');
      }
    }

    // 2. Verificar se o usuário está bloqueado
    const isBlocked = await this.isUserBlocked(roomId, userId);
    if (isBlocked) {
      violations.push('Usuário bloqueado nesta sala');
      maxSeverity = this.escalateSeverity(maxSeverity, 'critical');
    }

    // 3. Verificar comprimento da mensagem
    if (content.length > 2000) {
      violations.push('Mensagem muito longa');
      maxSeverity = this.escalateSeverity(maxSeverity, 'low');
    }

    // 4. Verificar flood temporal (muitas mensagens em pouco tempo)
    const recentMessages = await this.getUserRecentMessages(roomId, userId, 60000); // 1 minuto
    if (recentMessages.length > 10) {
      violations.push('Muitas mensagens em pouco tempo (flood)');
      maxSeverity = this.escalateSeverity(maxSeverity, 'high');
    }

    // Determinar ação sugerida
    let suggestedAction: 'allow' | 'warn' | 'delete' | 'flag' = 'allow';
    if (violations.length > 0) {
      switch (maxSeverity) {
        case 'low':
          suggestedAction = 'warn';
          break;
        case 'medium':
          suggestedAction = 'flag';
          break;
        case 'high':
          suggestedAction = 'delete';
          break;
        case 'critical':
          suggestedAction = 'delete';
          break;
      }
    }

    return {
      isAllowed: violations.length === 0,
      violations,
      suggestedAction,
      severity: maxSeverity
    };
  }

  // Função auxiliar para escalar severidade
  private escalateSeverity(
    current: 'low' | 'medium' | 'high' | 'critical',
    new_severity: 'low' | 'medium' | 'high' | 'critical'
  ): 'low' | 'medium' | 'high' | 'critical' {
    const levels = { low: 1, medium: 2, high: 3, critical: 4 };
    return levels[new_severity] > levels[current] ? new_severity : current;
  }

  // Verificar se usuário está bloqueado
  async isUserBlocked(roomId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('chat_blocked_users')
        .select('id, expires_at')
        .eq('room_id', roomId)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        // 42P01 = tabela inexistente. A migration de moderação
        // (20250815_chat_moderation.sql) nunca foi aplicada neste banco, e
        // avisar a cada mensagem enviada só entope o console — o que atrapalha
        // justamente quem está diagnosticando outra coisa. Sem a tabela não há
        // ninguém bloqueado, então seguir em frente é o comportamento correto.
        if (error.code === '42P01') {
          if (!moderacaoIndisponivelAvisada) {
            moderacaoIndisponivelAvisada = true;
            console.warn(
              'Moderação de chat desativada: a tabela chat_blocked_users não existe neste banco ' +
              '(aplique supabase/migrations/20250815_chat_moderation.sql para habilitar).'
            );
          }
          return false;
        }

        console.error('Erro ao verificar usuário bloqueado:', error);
        return false;
      }

      if (!data) return false;

      // Verificar se o bloqueio expirou
      if (data.expires_at) {
        const expiryDate = new Date(data.expires_at);
        if (expiryDate < new Date()) {
          // Bloqueio expirado, remover
          await this.unblockUser(roomId, userId, 'system');
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Erro ao verificar bloqueio:', error);
      return false;
    }
  }

  // Obter mensagens recentes do usuário
  private async getUserRecentMessages(roomId: string, userId: string, timeWindowMs: number) {
    const timeThreshold = new Date(Date.now() - timeWindowMs).toISOString();
    
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, created_at')
        .eq('room_id', roomId)
        .eq('user_auth_id', userId)
        .gte('created_at', timeThreshold)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar mensagens recentes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar mensagens recentes:', error);
      return [];
    }
  }

  // Deletar mensagem (apenas para admins)
  async deleteMessage(messageId: string, moderatorId: string, reason: string): Promise<boolean> {
    try {
      // Primeiro, buscar a mensagem para logs
      const { data: messageData, error: fetchError } = await supabase
        .from('chat_messages')
        .select('id, room_id, user_auth_id, content')
        .eq('id', messageId)
        .single();

      if (fetchError || !messageData) {
        console.error('Mensagem não encontrada:', fetchError);
        return false;
      }

      // Verificar se o moderador tem permissão
      const canModerate = await this.canUserModerate(messageData.room_id, moderatorId);
      if (!canModerate) {
        console.error('Usuário não tem permissão para moderar');
        return false;
      }

      // Registrar ação de moderação
      await this.logModerationAction({
        room_id: messageData.room_id,
        message_id: messageId,
        user_id: messageData.user_auth_id,
        moderator_id: moderatorId,
        action_type: 'delete_message',
        reason,
        metadata: { original_content: messageData.content }
      });

      // Deletar a mensagem
      const { error: deleteError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('id', messageId);

      if (deleteError) {
        console.error('Erro ao deletar mensagem:', deleteError);
        return false;
      }

      console.log('Mensagem deletada com sucesso:', messageId);
      return true;
    } catch (error) {
      console.error('Erro ao deletar mensagem:', error);
      return false;
    }
  }

  // Bloquear usuário
  async blockUser(
    roomId: string, 
    userId: string, 
    moderatorId: string, 
    reason: string, 
    durationHours?: number
  ): Promise<boolean> {
    try {
      // Verificar se o moderador tem permissão
      const canModerate = await this.canUserModerate(roomId, moderatorId);
      if (!canModerate) {
        console.error('Usuário não tem permissão para bloquear');
        return false;
      }

      let expiresAt: string | undefined;
      if (durationHours) {
        expiresAt = new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString();
      }

      // Inserir bloqueio
      const { error } = await supabase
        .from('chat_blocked_users')
        .insert({
          room_id: roomId,
          user_id: userId,
          blocked_by: moderatorId,
          reason,
          expires_at: expiresAt
        });

      if (error) {
        console.error('Erro ao bloquear usuário:', error);
        return false;
      }

      // Registrar ação
      await this.logModerationAction({
        room_id: roomId,
        user_id: userId,
        moderator_id: moderatorId,
        action_type: 'block_user',
        reason,
        metadata: { duration_hours: durationHours }
      });

      console.log('Usuário bloqueado com sucesso:', userId);
      return true;
    } catch (error) {
      console.error('Erro ao bloquear usuário:', error);
      return false;
    }
  }

  // Desbloquear usuário
  async unblockUser(roomId: string, userId: string, moderatorId: string): Promise<boolean> {
    try {
      // Verificar permissão (exceto para sistema automático)
      if (moderatorId !== 'system') {
        const canModerate = await this.canUserModerate(roomId, moderatorId);
        if (!canModerate) {
          console.error('Usuário não tem permissão para desbloquear');
          return false;
        }
      }

      const { error } = await supabase
        .from('chat_blocked_users')
        .delete()
        .eq('room_id', roomId)
        .eq('user_id', userId);

      if (error) {
        console.error('Erro ao desbloquear usuário:', error);
        return false;
      }

      // Registrar ação (se não for sistema)
      if (moderatorId !== 'system') {
        await this.logModerationAction({
          room_id: roomId,
          user_id: userId,
          moderator_id: moderatorId,
          action_type: 'unblock_user',
          reason: 'Desbloqueado por moderador'
        });
      }

      console.log('Usuário desbloqueado com sucesso:', userId);
      return true;
    } catch (error) {
      console.error('Erro ao desbloquear usuário:', error);
      return false;
    }
  }

  // Verificar se usuário pode moderar
  async canUserModerate(roomId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('chat_room_members')
        .select('role')
        .eq('room_id', roomId)
        .eq('user_auth_id', userId)
        .single();

      if (error || !data) {
        console.error('Erro ao verificar permissão de moderação:', error);
        return false;
      }

      return data.role === 'admin';
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      return false;
    }
  }

  // Registrar ação de moderação
  private async logModerationAction(action: Omit<ModerationAction, 'id' | 'created_at'>) {
    try {
      const { error } = await supabase
        .from('chat_moderation_actions')
        .insert({
          ...action,
          created_at: new Date().toISOString()
        });

      if (error) {
        console.error('Erro ao registrar ação de moderação:', error);
      }
    } catch (error) {
      console.error('Erro ao registrar ação:', error);
    }
  }

  // Obter histórico de moderação para uma sala
  async getModerationHistory(roomId: string, limit: number = 50): Promise<ModerationAction[]> {
    try {
      const { data, error } = await supabase
        .from('chat_moderation_actions')
        .select(`
          *,
          user:user_id(name, email),
          moderator:moderator_id(name, email)
        `)
        .eq('room_id', roomId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Erro ao buscar histórico de moderação:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      return [];
    }
  }

  // Obter usuários bloqueados em uma sala
  async getBlockedUsers(roomId: string): Promise<BlockedUser[]> {
    try {
      const { data, error } = await supabase
        .from('chat_blocked_users')
        .select(`
          *,
          user:user_id(name, email),
          blocked_by_user:blocked_by(name, email)
        `)
        .eq('room_id', roomId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários bloqueados:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar usuários bloqueados:', error);
      return [];
    }
  }

  // Reportar conteúdo inadequado
  async reportContent(
    messageId: string, 
    reporterId: string, 
    reason: string, 
    description?: string
  ): Promise<boolean> {
    try {
      const { data: messageData, error: fetchError } = await supabase
        .from('chat_messages')
        .select('room_id, user_auth_id')
        .eq('id', messageId)
        .single();

      if (fetchError || !messageData) {
        console.error('Mensagem não encontrada para report:', fetchError);
        return false;
      }

      // Registrar o report
      await this.logModerationAction({
        room_id: messageData.room_id,
        message_id: messageId,
        user_id: messageData.user_auth_id,
        moderator_id: reporterId,
        action_type: 'flag_content',
        reason,
        metadata: { description, reporter_id: reporterId }
      });

      console.log('Conteúdo reportado com sucesso:', messageId);
      return true;
    } catch (error) {
      console.error('Erro ao reportar conteúdo:', error);
      return false;
    }
  }
}

export const moderationService = new ModerationService();